import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Button,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StopIcon from '@mui/icons-material/Stop';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { fetchLiveSurveyById, setSurveyActiveStatus, setSurveyCurrentQuestion, moveToNextQuestion, moveToPreviousQuestion, fetchQuestionReport } from '../../controllers/liveSurveyController';
import createLiveSurveyConnection from '../../api/signalRClient';
import BackButton from '../../components/BackButton';

const LiveSurveyControl = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const connectionRef = useRef(null);
  const eventIdRef = useRef(null);

  const loadReport = useCallback(async (questionId) => {
    if (!surveyId || !questionId) return;
    setLoadingReport(true);
    try {
      const report = await fetchQuestionReport(surveyId, questionId);
      setReportData(report);
    } catch (e) {
      // Silent fail
    } finally {
      setLoadingReport(false);
    }
  }, [surveyId]);

  useEffect(() => {
    if (!surveyId) {
      setError('Anket ID bulunamadı');
      setLoading(false);
      return;
    }

    let active = true;
    const loadSurvey = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchLiveSurveyById(surveyId);
        if (!active) return;
        setSurvey(data);
        eventIdRef.current = data.eventId; // EventId'yi ref'e kaydet
        
        // Eğer aktif soru varsa raporu yükle
        if (data.isActive && data.currentQuestionIndex >= 0 && data.questions && data.questions.length > 0) {
          const sortedQuestions = [...data.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
          const currentQuestion = sortedQuestions[data.currentQuestionIndex];
          if (currentQuestion) {
            loadReport(currentQuestion.id);
          }
        }

        // WebSocket bağlantısını kur
        if (data.eventId) {
          try {
            const connection = createLiveSurveyConnection(data.eventId);
            connectionRef.current = connection;

            // Bağlantı durumu event'leri
            connection.onclose(() => {
              if (active) setConnectionStatus('Disconnected');
            });

            connection.onreconnecting(() => {
              if (active) setConnectionStatus('Reconnecting');
            });

            connection.onreconnected(() => {
              if (active) {
                setConnectionStatus('Connected');
                // Yeniden bağlandığında admin grubuna tekrar katıl
                connection.invoke('JoinAdminGroup', data.eventId).catch(() => {});
              }
            });

            // Admin status changed event'ini dinle
            // Backend'de 'ReceiveAdminSurveyStatus' event'i gönderiliyor
            connection.on('ReceiveAdminSurveyStatus', (statusDto) => {
              if (!active) return;
              
              // Survey durumunu güncelle
              if (statusDto.surveyId === data.id) {
                setSurvey((prev) => ({
                  ...prev,
                  isActive: statusDto.isActive,
                  currentQuestionIndex: statusDto.currentQuestionIndex,
                }));

                // Eğer aktif soru varsa ve rapor varsa güncelle
                if (statusDto.currentQuestion && statusDto.currentQuestionReport) {
                  setReportData(statusDto.currentQuestionReport);
                } else if (statusDto.currentQuestion) {
                  // Rapor yoksa manuel olarak yükle
                  loadReport(statusDto.currentQuestion.id);
                } else {
                  // Soru yoksa raporu temizle
                  setReportData(null);
                }
              }
            });

            // Bağlantıyı başlat
            connection.start()
              .then(() => {
                if (active) {
                  setConnectionStatus('Connected');
                  // Admin grubuna katıl
                  return connection.invoke('JoinAdminGroup', data.eventId);
                }
              })
              .catch((err) => {
                if (active) {
                  console.error('SignalR connection error:', err);
                  setConnectionStatus('Error');
                }
              });
          } catch (err) {
            console.error('Failed to create SignalR connection:', err);
            setConnectionStatus('Error');
          }
        }
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Anket yüklenemedi');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSurvey();

    return () => {
      active = false;
      // WebSocket bağlantısını kapat
      if (connectionRef.current && eventIdRef.current) {
        connectionRef.current.invoke('LeaveAdminGroup', eventIdRef.current)
          .then(() => connectionRef.current.stop())
          .catch(() => {
            // Hata olsa bile bağlantıyı kapat
            connectionRef.current.stop().catch(() => {});
          });
        connectionRef.current = null;
        eventIdRef.current = null;
      } else if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
      }
    };
  }, [surveyId, loadReport]);

  const handleStopSurvey = async () => {
    if (!survey?.id) return;
    try {
      await setSurveyActiveStatus(survey.id, false);
      const updated = await fetchLiveSurveyById(survey.id);
      setSurvey(updated);
    } catch (e) {
      alert(e?.message || 'Anket durdurulamadı');
    }
  };

  const handleNextQuestion = async () => {
    if (!survey?.id) return;
    try {
      await moveToNextQuestion(survey.id);
      const updated = await fetchLiveSurveyById(survey.id);
      setSurvey(updated);
      // Yeni soru için raporu yükle
      if (updated.currentQuestionIndex >= 0 && updated.questions && updated.questions.length > 0) {
        const sortedQuestions = [...updated.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
        const currentQuestion = sortedQuestions[updated.currentQuestionIndex];
        if (currentQuestion) {
          loadReport(currentQuestion.id);
        }
      }
    } catch (e) {
      alert(e?.message || 'Sonraki soruya geçilemedi');
    }
  };

  const handlePreviousQuestion = async () => {
    if (!survey?.id) return;
    try {
      await moveToPreviousQuestion(survey.id);
      const updated = await fetchLiveSurveyById(survey.id);
      setSurvey(updated);
      // Yeni soru için raporu yükle
      if (updated.currentQuestionIndex >= 0 && updated.questions && updated.questions.length > 0) {
        const sortedQuestions = [...updated.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
        const currentQuestion = sortedQuestions[updated.currentQuestionIndex];
        if (currentQuestion) {
          loadReport(currentQuestion.id);
        }
      }
    } catch (e) {
      alert(e?.message || 'Önceki soruya geçilemedi');
    }
  };

  const handleHideQuestions = async () => {
    if (!survey?.id) return;
    try {
      await setSurveyCurrentQuestion(survey.id, -1);
      const updated = await fetchLiveSurveyById(survey.id);
      setSurvey(updated);
      setReportData(null);
    } catch (e) {
      alert(e?.message || 'Sorular gizlenemedi');
    }
  };

  const handleShowFirstQuestion = async () => {
    if (!survey?.id) return;
    try {
      await setSurveyCurrentQuestion(survey.id, 0);
      const updated = await fetchLiveSurveyById(survey.id);
      setSurvey(updated);
      // İlk soru için raporu yükle
      if (updated.questions && updated.questions.length > 0) {
        const sortedQuestions = [...updated.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
        const firstQuestion = sortedQuestions[0];
        if (firstQuestion) {
          loadReport(firstQuestion.id);
        }
      }
    } catch (e) {
      alert(e?.message || 'İlk soru gösterilemedi');
    }
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 0: return 'Çoktan Seçmeli';
      case 1: return 'Değerlendirme (1-5)';
      case 2: return 'Sayı';
      default: return 'Bilinmeyen';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!survey) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Alert severity="warning">Anket bulunamadı</Alert>
      </Box>
    );
  }

  const sortedQuestions = survey.questions ? [...survey.questions].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
  const currentQuestion = survey.currentQuestionIndex >= 0 && sortedQuestions.length > 0
    ? sortedQuestions[survey.currentQuestionIndex]
    : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
      <Stack spacing={3} maxWidth="1200px" mx="auto">
        {/* Back Button */}
        <BackButton />

        {/* Header */}
        <Card
          sx={{
            border: '1px solid #f1f1f1',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            borderRadius: '8px',
            p: 3,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack spacing={1}>
              <Typography variant="h4" sx={{ fontSize: '18px', fontWeight: 500 }}>
                {survey.title}
              </Typography>
              {survey.description && (
                <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                  {survey.description}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={connectionStatus === 'Connected' ? 'Bağlı' : connectionStatus === 'Reconnecting' ? 'Yeniden Bağlanıyor...' : connectionStatus === 'Error' ? 'Bağlantı Hatası' : 'Bağlantı Yok'}
                  color={connectionStatus === 'Connected' ? 'success' : connectionStatus === 'Reconnecting' ? 'warning' : 'default'}
                  size="small"
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {survey.isActive ? (
                <>
                  <Chip label="Aktif" color="success" />
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={handleStopSurvey}
                    sx={{
                      textTransform: 'none',
                      fontSize: '14px',
                      borderRadius: '5px',
                      px: 2,
                    }}
                  >
                    Durdur
                  </Button>
                </>
              ) : (
                <Chip label="Pasif" color="default" />
              )}
            </Stack>
          </Stack>
        </Card>

        {/* Current Question Display */}
        {survey.isActive && currentQuestion ? (
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 3,
              borderLeft: '4px solid',
              borderLeftColor: '#0E4D94',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>
                  Soru {survey.currentQuestionIndex + 1} / {sortedQuestions.length}
                </Typography>
                <Chip label={getQuestionTypeLabel(currentQuestion.questionType)} size="small" />
              </Stack>
              <Typography variant="body1" sx={{ fontSize: '15px', fontWeight: 500 }}>
                {currentQuestion.questionText}
              </Typography>
              {currentQuestion.questionType === 0 && currentQuestion.options && currentQuestion.options.length > 0 && (
                <Box sx={{ pl: 2 }}>
                  <Stack spacing={1}>
                    {currentQuestion.options.map((opt, idx) => (
                      <Typography key={idx} variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                        {String.fromCharCode(65 + idx)}. {opt}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Card>
        ) : survey.isActive ? (
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Alert severity="info">
              Şu anda hiçbir soru gösterilmiyor. İlk soruyu göstermek için "İlk Soruyu Göster" butonuna tıklayın.
            </Alert>
          </Card>
        ) : (
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Alert severity="warning">
              Anket pasif durumda. Anketi başlatmak için ana sayfaya dönün.
            </Alert>
          </Card>
        )}

        {/* Controls */}
        {survey.isActive && (
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handlePreviousQuestion}
                disabled={survey.currentQuestionIndex <= 0}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '5px',
                  px: 2,
                }}
              >
                Önceki Soru
              </Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityOffIcon />}
                onClick={handleHideQuestions}
                disabled={survey.currentQuestionIndex < 0}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '5px',
                  px: 2,
                }}
              >
                Soruları Gizle
              </Button>
              <Button
                variant="contained"
                onClick={handleShowFirstQuestion}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '5px',
                  px: 2,
                  backgroundColor: '#0E4D94',
                  '&:hover': {
                    backgroundColor: '#0a3d7a',
                  },
                }}
              >
                İlk Soruyu Göster
              </Button>
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNextQuestion}
                disabled={survey.currentQuestionIndex >= sortedQuestions.length - 1}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '5px',
                  px: 2,
                }}
              >
                Sonraki Soru
              </Button>
            </Stack>
          </Card>
        )}

        {/* Report */}
        {survey.isActive && currentQuestion && reportData && (
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>
                Canlı Rapor
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                Toplam Cevaplar: {reportData.totalAnswers || 0}
              </Typography>

              {reportData.questionType === 0 && reportData.optionCounts && (
                <Grid container spacing={2}>
                  {Object.entries(reportData.optionCounts).map(([index, count]) => {
                    const optionIndex = Number(index);
                    const optionText = currentQuestion.options?.[optionIndex] || `Seçenek ${optionIndex + 1}`;
                    const percentage = reportData.totalAnswers > 0
                      ? ((count / reportData.totalAnswers) * 100).toFixed(1)
                      : 0;
                    return (
                      <Grid item xs={12} sm={6} key={index}>
                        <Card
                          sx={{
                            border: '1px solid #f1f1f1',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            borderRadius: '8px',
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 500 }}>
                                {String.fromCharCode(65 + optionIndex)}. {optionText}
                              </Typography>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: '#0E4D94' }}>
                                  {count}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                                  %{percentage}
                                </Typography>
                              </Stack>
                              <Box
                                sx={{
                                  height: 28,
                                  bgcolor: '#f5f5f5',
                                  border: '1px solid #e6e6e6',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${percentage}%`,
                                    bgcolor: '#0E4D94',
                                    borderRadius: '6px',
                                    transition: 'width 0.3s',
                                  }}
                                />
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {reportData.questionType === 1 && reportData.ratingStats && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Ortalama</Typography>
                        <Typography variant="h4" sx={{ fontSize: '24px', fontWeight: 600, color: '#0E4D94' }}>
                          {reportData.ratingStats.averageRating?.toFixed(2) || '0.00'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Minimum</Typography>
                        <Typography variant="h4" sx={{ fontSize: '24px', fontWeight: 600 }}>
                          {reportData.ratingStats.minRating || 1}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Maksimum</Typography>
                        <Typography variant="h4" sx={{ fontSize: '24px', fontWeight: 600 }}>
                          {reportData.ratingStats.maxRating || 5}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  {reportData.ratingStats.ratingCounts && (
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        {Object.entries(reportData.ratingStats.ratingCounts)
                          .sort(([a], [b]) => Number(b) - Number(a))
                          .map(([rating, count]) => {
                            const percentage = reportData.totalAnswers > 0
                              ? ((count / reportData.totalAnswers) * 100).toFixed(1)
                              : 0;
                            return (
                              <Box key={rating}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography variant="body1" sx={{ fontSize: '14px' }}>
                                    {rating} Yıldız
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500 }}>
                                    {count} (%{percentage})
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: 28,
                                    bgcolor: '#f5f5f5',
                                    border: '1px solid #e6e6e6',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: `${percentage}%`,
                                      bgcolor: '#FF9800',
                                      borderRadius: '6px',
                                      transition: 'width 0.3s',
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              )}

              {reportData.questionType === 2 && reportData.numberStats && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Ortalama</Typography>
                        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {reportData.numberStats.average?.toFixed(2) || '0.00'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Toplam</Typography>
                        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {reportData.numberStats.sum?.toFixed(2) || '0.00'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Minimum</Typography>
                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 500 }}>
                          {reportData.numberStats.min?.toFixed(2) || '0.00'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Maksimum</Typography>
                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 500 }}>
                          {reportData.numberStats.max?.toFixed(2) || '0.00'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        border: '1px solid #f1f1f1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: '8px',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>Medyan</Typography>
                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 500 }}>
                          {reportData.numberStats.median?.toFixed(2) || '0.00'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {loadingReport && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* Questions List */}
        {sortedQuestions.length > 0 && (
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 2 }}>
              Tüm Sorular ({sortedQuestions.length})
            </Typography>
            <Stack spacing={1}>
              {sortedQuestions.map((q, idx) => (
                <Card
                  key={q.id}
                  sx={{
                    border: '1px solid #f1f1f1',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    borderRadius: '8px',
                    p: 2,
                    bgcolor: survey.currentQuestionIndex === idx ? '#E7EDF4' : 'background.paper',
                    borderLeft: survey.currentQuestionIndex === idx ? '4px solid' : 'none',
                    borderLeftColor: survey.currentQuestionIndex === idx ? '#0E4D94' : 'transparent',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: survey.currentQuestionIndex === idx ? 500 : 400 }}>
                      {idx + 1}. {q.questionText}
                    </Typography>
                    {survey.currentQuestionIndex === idx && (
                      <Chip label="Aktif" size="small" sx={{ backgroundColor: '#0E4D94', color: 'white' }} />
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default LiveSurveyControl;

